import { api } from 'constants/API';
import { PLATTS_CONNECT, TestLocators } from 'constants/Locators';

import { SelectChangeEvent } from '@mui/material';
import axios from 'axios';
import { DarkThemeContext, MUI, ThemeMode } from 'ci-esi-web-shared';
import { CSSProperties, MouseEvent, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import Cookies from 'universal-cookie';

import { actions as oktaActions } from 'app/components/pages/OktaLoginPage/slice';
import { selectUsername, userAvailableDatabases, userSelectedDatabase } from 'app/containers/PrivateRoute/selectors';
import { actions } from 'app/containers/PrivateRoute/slice';
import { setSelectedDatabaseByOrigin } from 'app/containers/PrivateRoute/utils';
import { EDatabaseName } from 'helpers/constants';
import { clearLastTokenRefreshDate } from 'helpers/cookies';
import { domainName } from 'helpers/domain';
import { translations } from 'locales/i18n';
import { colors } from 'styles/theme/colors';
import { SELECTED_DATABASE } from 'utils/cookie';

export interface ProfileMenuBodyProps {
  onClose: MUI.PopoverProps['onClose'];
}
export interface LogoutRequestBody {
  logoutPageUrl?: string;
  source: string;
  domain: string;
}

export function ProfileMenuBody({ onClose }: ProfileMenuBodyProps) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const username = useSelector(selectUsername);
  const availableDatabasesForUser = useSelector(userAvailableDatabases);
  const selectedDatabaseFromRedux = useSelector(userSelectedDatabase);
  const { setDarkMode } = useContext(DarkThemeContext);
  const cookies = useMemo(() => new Cookies(), []);
  const [selectedDatabase, setSelectedDatabase] = useState(selectedDatabaseFromRedux);
  const theme = MUI.useTheme();
  const isDarkMode = theme.palette.mode === ThemeMode.DARK;

  const isInIframe = window.self !== window.top;
  const isFromPlattsConnect = isInIframe || window.location.href.includes(PLATTS_CONNECT);

  const menuItemSx = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    padding: 1.25,
    width: 208,
    cursor: 'default',
    '&:hover': { backgroundColor: 'inherit' },
  };

  const labelSx = {
    fontWeight: 400,
    color: isDarkMode ? colors.baseGrey20 : colors.grey42,
    fontSize: '12px',
  };

  const valueSx = {
    fontWeight: 400,
    color: isDarkMode ? colors.baseGrey10 : colors.grey51,
    paddingLeft: 0.75,
    overflowWrap: 'break-word',
    fontSize: '12px',
  };

  const databaseLabelSx = { ...labelSx, fontSize: '14px' };
  const databaseValueSx = { ...valueSx, fontSize: '14px' };

  const textFieldBorderSx = {
    background: isDarkMode ? colors.baseGrey80 : colors.baseGrey05,
    '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
    '& .MuiOutlinedInput-input': {
      backgroundColor: isDarkMode ? 'inherit' : colors.baseGrey05,
    },
    '& .MuiInputLabel-outlined': {
      fontSize: '16px',
      marginLeft: '7px',
      color: 'gray',
      fontWeight: 400,
    },
  };

  const rootMenuItemSx = {
    '&:hover, &.Mui-selected, &.Mui-selected:hover': {
      backgroundColor: isDarkMode ? colors.grey54 : colors.grey12,
      color: isDarkMode ? colors.grey12 : colors.grey54,
    },
    fontSize: '16px',
    backgroundColor: isDarkMode ? colors.grey56 : colors.baseGrey05,
    color: isDarkMode ? colors.grey45 : colors.baseGrey35,
    fontFamily: '"Akkurat", Arial, sans-serif',
    fontWeight: 400,
    padding: '10px',
    cursor: 'default',
  };

  const menuItemBorderSx = {
    border: '1px solid',
    overflowY: 'scroll',
    borderColor: isDarkMode ? colors.grey56 : colors.grey15,
  };

  const menuItemPaddingSx = { paddingBottom: '0px', paddingTop: '4px' };

  const selectStyleSx = {
    fontWeight: 400,
    color: isDarkMode ? colors.grey12 : colors.grey54,
    cursor: 'default',
  };

  const dataItemSx = {
    display: 'flex',
    flexWrap: 'wrap',
    width: '100%',
    overflowWrap: 'break-word',
  };

  const buttonSx = { boxShadow: 'none', borderRadius: 1, height: 32, border: '1px solid rgba(255, 255, 255, 0.23)' };

  const dividerSx = { width: '100%', margin: theme.spacing(2, 0) };

  const handleThemeChange = useCallback(
    (event: SelectChangeEvent<ThemeMode>) => {
      if (onClose) {
        onClose(event, 'backdropClick');
      }
      setDarkMode(event.target.value === ThemeMode.DARK);
    },
    [setDarkMode, onClose],
  );

  const handleDatabaseChange = useCallback(
    (event: SelectChangeEvent<string>) => {
      if (onClose) {
        onClose(event, 'backdropClick');
      }
      cookies.set(SELECTED_DATABASE, event.target.value, { path: '/', domain: domainName() });
      dispatch(actions.setSelectedDatabase(event.target.value as EDatabaseName));
      setSelectedDatabase(event.target.value as EDatabaseName);
    },
    [cookies, dispatch, onClose],
  );

  useEffect(() => {
    const database = setSelectedDatabaseByOrigin(selectedDatabase as EDatabaseName, availableDatabasesForUser);
    cookies.set(SELECTED_DATABASE, database, { path: '/', domain: domainName() });
    setSelectedDatabase(database);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleLogout = useCallback(
    (event: MouseEvent<HTMLButtonElement>) => {
      if (onClose) {
        onClose(event, 'backdropClick');
      }
      const logoutRequestData: LogoutRequestBody = {
        logoutPageUrl: window.location.toString(),
        source: 'Logout Button',
        domain: domainName(),
      };

      dispatch(oktaActions.clearUserInfoFromOkta());
      clearLastTokenRefreshDate();
      document.cookie = `${SELECTED_DATABASE}= ; Domain=${domainName()}; path=/;`;
      axios.delete(api.logout, { data: logoutRequestData }).finally(() => {
        window.location.href = `${process.env.REACT_APP_IHS_AUTH_URL}/clearsession`;
      });
    },
    [dispatch, onClose],
  );

  return (
    <MUI.MenuItem
      style={{ backgroundColor: 'transparent', color: 'white' }}
      sx={menuItemSx}
      disableRipple
      data-testid={TestLocators.UserProfile_Menu}
    >
      <div style={{ width: '100%' }}>
        <MUI.Typography
          variant="body1"
          component="span"
          sx={labelSx}
          data-testid={`${TestLocators.UserProfile_Menu}_Logged_Label`}
        >
          {t(translations.PROFILE.LOGGED_LABEL)}
        </MUI.Typography>
        <MUI.Typography
          variant="body1"
          component="span"
          noWrap={false}
          style={{ display: 'content', whiteSpace: 'pre-line', width: '100%' }}
          sx={valueSx}
          title={username}
          data-testid={`${TestLocators.UserProfile_Menu}_Username`}
        >
          <div>{username}</div>
        </MUI.Typography>
      </div>

      {availableDatabasesForUser.length === 1 ? (
        <div style={dataItemSx as CSSProperties}>
          <MUI.Typography variant="body1" component="span" sx={databaseLabelSx}>
            {t(translations.PROFILE.DATABASE_LABEL)}
            {':'}
          </MUI.Typography>
          <MUI.Typography
            variant="body1"
            component="span"
            sx={databaseValueSx}
            data-testid={`${TestLocators.UserProfile_Menu}_Database`}
            gutterBottom
          >
            {selectedDatabase}
          </MUI.Typography>
        </div>
      ) : (
        <div style={{ width: '100%', margin: '16px 0' }}>
          <MUI.FormControl variant="outlined" fullWidth sx={textFieldBorderSx}>
            <MUI.InputLabel id="change-theme-label">{t(translations.PROFILE.DATABASE_LABEL)}</MUI.InputLabel>
            <MUI.Select
              labelId="change-theme-label"
              id="change-theme"
              value={selectedDatabase}
              onChange={handleDatabaseChange}
              label={t(translations.PROFILE.DATABASE_LABEL)}
              variant="outlined"
              MenuProps={{
                anchorOrigin: {
                  vertical: 'bottom',
                  horizontal: 'left',
                },
                transformOrigin: {
                  vertical: 'top',
                  horizontal: 'left',
                },
                PaperProps: { sx: menuItemBorderSx },
                MenuListProps: {
                  sx: menuItemPaddingSx,
                },
              }}
              sx={selectStyleSx}
            >
              {availableDatabasesForUser.map((databaseName: string) => {
                return (
                  <MUI.MenuItem value={databaseName} key={databaseName} sx={rootMenuItemSx}>
                    {databaseName}
                  </MUI.MenuItem>
                );
              })}
            </MUI.Select>
          </MUI.FormControl>
        </div>
      )}
      <div style={{ width: '100%', marginTop: '4px' }}>
        <MUI.FormControl variant="outlined" fullWidth sx={textFieldBorderSx}>
          <MUI.InputLabel id="change-theme-label">{t(translations.PROFILE.THEME_LABEL)}</MUI.InputLabel>
          <MUI.Select
            labelId="change-theme-label"
            id="change-theme"
            value={isDarkMode ? ThemeMode.DARK : ThemeMode.LIGHT}
            onChange={handleThemeChange}
            label={t(translations.PROFILE.THEME_LABEL)}
            variant="outlined"
            MenuProps={{
              anchorOrigin: {
                vertical: 'bottom',
                horizontal: 'left',
              },
              transformOrigin: {
                vertical: 'top',
                horizontal: 'left',
              },
              PaperProps: { sx: menuItemBorderSx },
              MenuListProps: {
                sx: menuItemPaddingSx,
              },
            }}
            sx={selectStyleSx}
          >
            <MUI.MenuItem value={ThemeMode.DARK} sx={rootMenuItemSx}>
              {t(translations.PROFILE.DARK_MODE)}
            </MUI.MenuItem>
            <MUI.MenuItem value={ThemeMode.LIGHT} sx={rootMenuItemSx}>
              {t(translations.PROFILE.LIGHT_MODE)}
            </MUI.MenuItem>
          </MUI.Select>
        </MUI.FormControl>
      </div>
      {!isFromPlattsConnect && (
        <>
          <MUI.Divider sx={dividerSx} />
          <MUI.Box sx={dataItemSx}>
            <MUI.Button
              sx={buttonSx}
              fullWidth
              variant="outlined"
              color="inherit"
              onClick={handleLogout}
              data-testid={`${TestLocators.UserProfile_Menu}_Logout_Btn`}
            >
              {t(translations.PROFILE.LOGOUT)}
            </MUI.Button>
          </MUI.Box>
        </>
      )}
    </MUI.MenuItem>
  );
}
